import NewClassBadge from '@/components/NewClassBadge'
import SponsorLogo from '@/components/SponsorLogo'
import { rbp } from '@/constants/measured-scope'
import { getConfig } from '@/lib/config'
import Badges, { BadgesSize, BadgesTheme } from '@/widgets/Badges'
import { useMediaQuery } from '@react-hookz/web'
import classNames from 'classnames/bind'
import { useRouter } from 'next/router'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import * as styles from './style'

const cx = classNames.bind(styles)

export type Theme = 'primary' | 'secondary' | 'standard' | 'primaryMuted' | 'tertiary' | 'classic'
export type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export const Divider = (): JSX.Element => <hr className={styles.divider} />

const { publicRuntimeConfig } = getConfig()
const BASE_URL = publicRuntimeConfig.website.url
export interface Props {
  theme?: Theme
  children?: React.ReactNode | JSX.Element
  // Don't use this prop. It does absolutely nothing. Choose to size cards
  // by wrapping it in a div and set the size on that div. It's a better solution.
  // Also, simplifies the component
  size?: Size
  footer?: React.ReactNode
  header?: React.ReactNode
  overlay?: boolean
  backgroundImage?: string | null
  sponsorLogo?: string | null
  bannerImage?: string | null
  accessOverlay?: boolean | null
  iconImage?: string | null
  href?: string
  className?: string
  onClick?: () => void
  shadow?: boolean
  // Fluid removes the width restrictions and allows the card to be the width of its parent
  fluid?: boolean
  // Wide is a special case that produces a new variant of a long card for desktop views
  wide?: boolean
  fixedAspectRatio?: boolean
  showFallbackImage?: boolean
  isSchedule?: boolean
  overflow?: boolean
  zoomImage?: boolean
  instructorAsImgHeader?: boolean
  guideInfoFooter?: boolean
  blurredImgHeader?: boolean
  isCommunityLedClass?: boolean
  isEncore?: boolean
  classIsNew?: boolean
  instructorCard?: boolean
  title?: string
}

const Card = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      theme = 'standard',
      overlay = true,
      children,
      footer,
      header,
      backgroundImage,
      sponsorLogo,
      accessOverlay,
      iconImage,
      bannerImage,
      href,
      className = '',
      shadow = false,
      onClick,
      fluid,
      wide,
      fixedAspectRatio,
      showFallbackImage,
      overflow = true,
      isSchedule = false,
      zoomImage = true,
      instructorAsImgHeader = false,
      guideInfoFooter = false,
      blurredImgHeader = false,
      isCommunityLedClass = false,
      isEncore = false,
      classIsNew = false,
      instructorCard = false,
      title,
    }: Props,
    ref,
  ): ReactElement | null => {
    const router = useRouter()
    const classes = cx({
      'gsu-card': true, // Helpful generic class to target for wrapping contexts and layout
      root: true,
      [styles[`${theme}`]]: true,
      whiteText: !!backgroundImage,
      overflow,
      hasBackgroundImage: !!backgroundImage || !!showFallbackImage,
      hasFooter: !!footer,
      hasHeader: !!header,
      isLink: !!onClick || !!href,
      shadow,
      fluid,
      wide,
      fixedAspectRatio,
      schedule: isSchedule,
      [className]: true,
      zoomImage,
      instructorAsImgHeader,
      guideInfoFooter,
      blurredImgHeader,
      instructorCard,
    })
    const getBackgroundImage = useMemo(() => {
      if (!backgroundImage && showFallbackImage) {
        return {
          backgroundImage: `url("${'/images/image-placeholder-large.png'}")`,
        }
      }

      if (backgroundImage) {
        return {
          backgroundImage: `url("${backgroundImage}")`,
        }
      }

      return {}
    }, [backgroundImage, showFallbackImage])

    const getBannerImage = useMemo(() => {
      if (!bannerImage && showFallbackImage) {
        return {
          backgroundImage: `url("${'/images/image-placeholder-large.png'}")`,
        }
      }

      if (bannerImage) {
        return {
          backgroundImage: `url("${bannerImage}")`,
        }
      }

      return {}
    }, [bannerImage, showFallbackImage])

    //Memo used for alt tags for backgroundImage urls
    const altImageText = useMemo(() => {
      if (!title) {
        return `empty title`
      }

      return `Image thumbnail from class ${title}`
    }, [title])
    const accessOverlayStyle: string | undefined = accessOverlay
      ? `${styles.lockedDisplay}`
      : `${styles.backgroundImage}`

    const handleOnClick = useCallback(() => {
      if (onClick) {
        onClick()
      }

      if (href) {
        // Checking if the URL is on our website or not. If it is, we can use next.js routing.
        try {
          const newUrl = new URL(href)
          if (newUrl.href.startsWith(BASE_URL)) {
            router.push(href)
          } else {
            window.location.href = href
          }
        } catch (error) {
          window.location.href = href
        }
      }
    }, [])

    // Checking if the card is having focus for expanding the badges
    const [inFocus, setInFocus] = useState<boolean>(false)
    // Badge sizes
    const isDesktop = useMediaQuery(`only screen and (min-width : ${rbp.tablet})`, { initializeWithValue: false })
    const badgesSize: BadgesSize = isDesktop ? 'MD' : 'SM'
    const badgesTheme: BadgesTheme = isDesktop ? 'FULL_ON_FOCUS' : 'FULL'

    return (
      <div
        data-testid="card"
        ref={ref}
        className={classes}
        onMouseEnter={() => setInFocus(true)}
        onMouseLeave={() => setInFocus(false)}
      >
        {iconImage && !wide && <img src={iconImage} className={`mt-sm mr-sm ${styles.iconImage}`} />}
        <div
          className={cx(styles.contentWrapper, styles.action) + ' ' + `${overflow ? styles.overflow : ''}`}
          onClick={handleOnClick}
        >
          {overlay && <div className={styles.overlay}></div>}
          {(backgroundImage || showFallbackImage) && (
            <span className={accessOverlayStyle} style={getBackgroundImage} role="img" aria-label={altImageText} />
          )}
          {sponsorLogo && <SponsorLogo image={sponsorLogo} withHeading={!wide} />}
          <NewClassBadge classIsNew={classIsNew} className={styles.newBadge} />

          {bannerImage && <span className={styles.bannerImage} style={getBannerImage} />}
          {children ? <div className={styles.content}>{children}</div> : null}
          {iconImage && wide && (
            <span className={styles.iconImageContainer}>
              <img src={iconImage} alt={title} />
            </span>
          )}
          {!iconImage && !wide && (
            <Badges
              className={styles.badges}
              inFocus={inFocus}
              isCommunityLedClass={isCommunityLedClass}
              isEncore={isEncore}
              size={badgesSize}
              theme={badgesTheme}
            />
          )}
        </div>
        {header && (
          <header data-testid="card-header" className={styles.header}>
            <div className={styles.action}>{header}</div>
          </header>
        )}
        {instructorAsImgHeader && backgroundImage && (
          <div className={styles.action} onClick={handleOnClick}>
            <span
              className={styles.guideCoverImage}
              style={getBackgroundImage}
              role="img"
              aria-label="Guide profile picture"
            />
          </div>
        )}
        {footer && (
          <footer data-testid="card-footer" className={styles.footer}>
            <div className={`${styles.action} ${wide ? 'm-auto' : ''}`}>{footer}</div>
          </footer>
        )}
      </div>
    )
  },
)
export default Card
